import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || (
  import.meta.env.MODE === 'production' 
    ? 'https://api.tibbbi.com' 
    : 'http://localhost:8787'
);

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true
});

export interface Question {
  id: string;
  type: 'text' | 'single-choice' | 'multiple-choice' | 'numeric' | 'date' | 'body-map' | 'pain-scale' | 'time' | 'medication-entry' | 'vitals' | 'consent' | 'height' | 'weight';
  question: string;
  options?: string[];
  required?: boolean;
  min?: number;
  max?: number;
  placeholder?: string;
}

export interface QuestionnaireResponse {
  question?: Question;
  isComplete: boolean;
  sessionId: string;
  organizationName?: string;
  completedAt?: string;
  message?: string;
  needsInitialization?: boolean;
  status?: string;
  state?: 'generating' | 'in_progress' | 'completed' | 'error';
  chiefComplaint?: string;
  progress?: {
    current: number;
    total: number;
    answered?: number;
  };
  currentIndex?: number;
  totalQuestions?: number;
  hasAnswer?: boolean;
  existingAnswer?: any;
  canGoBack?: boolean;
  canGoForward?: boolean;
}

export interface SessionCompletionData {
  organizationName: string;
  completedAt?: string;
  sessionId: string;
}

export interface Answer {
  questionId: string;
  answer: any;
  sessionId: string;
  questionIndex?: number;
}

export interface ReferralData {
  patientName: string;
  patientEmail: string;
  patientPhone: string;
  providerName: string;
  providerClinic: string;
  referralCode: string;
  language: string;
  timestamp: string;
}

export const submitReferral = async (data: ReferralData): Promise<{ success: boolean; code: string }> => {
  try {
    const response = await api.post('/api/referral', data);
    return response.data;
  } catch (error) {
    // Log error but return success for demo purposes
    console.error('Referral submission error:', error);
    return { success: true, code: data.referralCode };
  }
};

export const questionnaireApi = {
  startSession: async (patientId?: string): Promise<QuestionnaireResponse> => {
    const response = await api.post('/api/v1/questionnaire/start', { patientId });
    return response.data;
  },

  submitAnswer: async (answer: Answer): Promise<QuestionnaireResponse> => {
    const response = await api.post('/api/v1/questionnaire/answer', answer);
    return response.data;
  },
  
  updateAnswer: async (answer: Answer): Promise<QuestionnaireResponse> => {
    const response = await api.put('/api/v1/questionnaire/answer', answer);
    return response.data;
  },

  getSession: async (sessionId: string): Promise<QuestionnaireResponse> => {
    const response = await api.get(`/api/v1/questionnaire/session/${sessionId}`);
    return response.data;
  },
  
  getQuestionByIndex: async (sessionId: string, index: number): Promise<QuestionnaireResponse> => {
    const response = await api.get(`/api/v1/questionnaire/session/${sessionId}/question/${index}`);
    return response.data;
  },

  initializeSession: async (sessionId: string): Promise<QuestionnaireResponse> => {
    const response = await api.post(`/api/v1/questionnaire/session/${sessionId}/initialize`);
    return response.data;
  },

  getCompletionData: async (sessionId: string): Promise<SessionCompletionData> => {
    const response = await api.get(`/api/v1/questionnaire/session/${sessionId}/completion`);
    return response.data;
  },

  checkRunStatus: async (sessionId: string): Promise<{ status: 'generating' | 'ready' | 'error'; questionsCount?: number; runStatus?: string; error?: string }> => {
    const response = await api.get(`/api/v1/webhooks/whatsapp/check-run-status/${sessionId}`);
    return response.data;
  }
};

export default api;